import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

// Firebase Functions
const saveAnalysis = httpsCallable(functions, 'saveAnalysis');
const loadAnalysis = httpsCallable(functions, 'loadAnalysis');
const deleteAnalysis = httpsCallable(functions, 'deleteAnalysis');
const listUserAnalyses = httpsCallable(functions, 'listUserAnalyses');

export interface Analysis {
  id?: string;
  name: string;
  description?: string;
  type: 'frequency_analysis' | 'fourier_transform' | 'audio_processing' | 'wave_generation';
  data: {
    waveData?: any[];
    audioData?: any;
    analysisResults?: any;
    metadata?: any;
  };
  tags?: string[];
  isPublic?: boolean;
  userId?: string;
  createdAt?: any;
  updatedAt?: any;
  lastAccessedAt?: any;
  version?: string;
  source?: 'personal' | 'shared';
}

export interface AnalysisListOptions {
  limit?: number;
  offset?: number;
  type?: 'frequency_analysis' | 'fourier_transform' | 'audio_processing' | 'wave_generation';
  includeShared?: boolean;
  tags?: string[];
}

class AnalysisService {
  // Save analysis
  async saveAnalysis(analysis: {
    name: string;
    description?: string;
    type: 'frequency_analysis' | 'fourier_transform' | 'audio_processing' | 'wave_generation';
    data: any;
    tags?: string[];
    isPublic?: boolean;
  }): Promise<string> {
    try {
      const result = await saveAnalysis(analysis);
      const data = result.data as { success: boolean; error?: string; analysisId?: string };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to save analysis');
      }
      
      return data.analysisId as string;
    } catch (error) {
      console.error('Save analysis error:', error);
      throw error;
    }
  }

  // Load analysis
  async loadAnalysis(analysisId: string, source: 'personal' | 'shared' = 'personal'): Promise<Analysis> {
    try {
      const result = await loadAnalysis({ analysisId, source });
      const data = result.data as { success: boolean; error?: string; analysis?: Analysis };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to load analysis');
      }
      
      return data.analysis as Analysis;
    } catch (error) {
      console.error('Load analysis error:', error);
      throw error;
    }
  }

  // Delete analysis
  async deleteAnalysis(analysisId: string, deleteFromShared: boolean = false): Promise<void> {
    try {
      const result = await deleteAnalysis({ analysisId, deleteFromShared });
      const data = result.data as { success: boolean; error?: string };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to delete analysis');
      }
    } catch (error) {
      console.error('Delete analysis error:', error);
      throw error;
    }
  }

  // List user analyses
  async listAnalyses(options: AnalysisListOptions = {}): Promise<{
    analyses: Analysis[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      const result = await listUserAnalyses(options);
      const data = result.data as { 
        success: boolean; 
        error?: string; 
        analyses?: Analysis[];
        total?: number;
        hasMore?: boolean;
      };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to list analyses');
      }
      
      return {
        analyses: data.analyses || [],
        total: data.total || 0,
        hasMore: data.hasMore || false
      };
    } catch (error) {
      console.error('List analyses error:', error);
      throw error;
    }
  }

  // Quick save current analysis
  async quickSaveAnalysis(
    type: Analysis['type'],
    data: any,
    name?: string
  ): Promise<string> {
    const timestamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const analysisName = name || `${type} ${timestamp}`;
    
    return this.saveAnalysis({
      name: analysisName,
      description: 'Auto-saved analysis',
      type,
      data,
      tags: ['auto-save', type],
      isPublic: false
    });
  }

  // Export analysis as JSON
  exportAnalysis(analysis: Analysis): string {
    const exportData = {
      name: analysis.name,
      description: analysis.description,
      type: analysis.type,
      data: analysis.data,
      tags: analysis.tags,
      version: '1.0',
      exportedAt: new Date().toISOString(),
      source: 'Resynth Lab'
    };
    
    return JSON.stringify(exportData, null, 2);
  }

  // Import analysis from JSON
  importAnalysis(jsonData: string): Analysis {
    try {
      const data = JSON.parse(jsonData);
      
      if (!data.name || !data.type || !data.data) {
        throw new Error('Invalid analysis format');
      }
      
      return {
        name: data.name,
        description: data.description || '',
        type: data.type,
        data: data.data,
        tags: data.tags || [],
        isPublic: false
      };
    } catch (error) {
      console.error('Import analysis error:', error);
      throw new Error('Failed to import analysis');
    }
  }

  // Generate analysis summary
  generateSummary(analysis: Analysis): string {
    const { type, data } = analysis;
    
    switch (type) {
      case 'frequency_analysis':
        if (data.analysisResults) {
          const { dominantFrequency, totalEnergy, spectralCentroid } = data.analysisResults;
          return `Dominant frequency: ${dominantFrequency.toFixed(2)}Hz, Total energy: ${totalEnergy.toFixed(4)}, Spectral centroid: ${spectralCentroid.toFixed(2)}Hz`;
        }
        break;
        
      case 'wave_generation':
        if (data.metadata) {
          const { sampleCount, duration, sampleRate } = data.metadata;
          return `Generated ${sampleCount} samples over ${duration}s at ${sampleRate}Hz`;
        }
        break;
        
      case 'audio_processing':
        if (data.audioData) {
          const { duration, sampleRate } = data.audioData;
          return `Processed ${duration}s audio at ${sampleRate}Hz`;
        }
        break;
        
      case 'fourier_transform':
        if (data.analysisResults) {
          const harmonicCount = data.analysisResults.harmonics?.length || 0;
          return `Fourier analysis with ${harmonicCount} significant harmonics`;
        }
        break;
        
      default:
        return 'Analysis data available';
    }
    
    return 'No summary available';
  }
}

export default new AnalysisService();